import type { SupabaseClient } from './SupabaseClient';
import { LRUCache } from '../core/LRUCache';
import type { Tables } from './database.types';

type ClusterEntity = Tables<'cluster_content'>;

export interface ClusterContent {
  readonly stars: ClusterEntity[];
  readonly planets: ClusterEntity[];
}

const CACHE_CAPACITY = 64;

/**
 * Loads star_systems and planets per cluster from the cluster_content view.
 * Non-blocking — results arrive asynchronously after the cluster hex grid
 * is already visible. Uses LRU cache to avoid re-fetching.
 */
export class ContentLoader {
  private _supabase: SupabaseClient;
  private _worldId: string;
  private _cache = new LRUCache<string, ClusterContent>(CACHE_CAPACITY);
  private _pending = new Set<string>();

  constructor(supabase: SupabaseClient, worldId: string) {
    this._supabase = supabase;
    this._worldId = worldId;
  }

  /** Returns cached content for a cluster, or null if not loaded yet. */
  getContent(clusterCol: number, clusterRow: number): ClusterContent | null {
    const key = `${clusterCol},${clusterRow}`;
    return this._cache.get(key) ?? null;
  }

  /** Request content for a set of cluster coordinates. Skips cached/pending. */
  requestClusters(clusters: readonly { col: number; row: number }[]): void {
    for (let i = 0; i < clusters.length; i++) {
      const { col, row } = clusters[i];
      const key = `${col},${row}`;
      if (this._cache.has(key) || this._pending.has(key)) continue;
      this._pending.add(key);
      this._loadCluster(col, row, key);
    }
  }

  private async _loadCluster(col: number, row: number, key: string): Promise<void> {
    try {
      const { data, error } = await this._supabase
        .from('cluster_content')
        .select('*')
        .eq('world_id', this._worldId)
        .eq('cluster_col', col)
        .eq('cluster_row', row);

      if (error || !data) {
        this._pending.delete(key);
        return;
      }

      const stars: ClusterEntity[] = [];
      const planets: ClusterEntity[] = [];
      for (let i = 0; i < data.length; i++) {
        if (data[i].entity_type === 'star') stars.push(data[i]);
        else planets.push(data[i]);
      }

      this._cache.set(key, { stars, planets });
    } finally {
      this._pending.delete(key);
    }
  }

  get cacheSize(): number { return this._cache.size; }
  get pendingCount(): number { return this._pending.size; }

  dispose(): void {
    this._cache.clear();
    this._pending.clear();
  }
}
